import React, { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({
  show,
  onClose,
  children,
  title,
  size = 'md',
  footer,
  className = '',
  isDarkMode = false,
  headerActions = null,
}) => {
  const dialogRef = useRef(null);

  // Prevent scrolling of the background when modal is open
  // and track open modal count to handle multiple modals properly
  useEffect(() => {
    window.openModalCount = window.openModalCount || 0;
    window.modalZIndexBase = window.modalZIndexBase || 1000;

    if (show) {
      window.openModalCount++;
      window.modalZIndexBase += 10;
      document.body.style.overflow = 'hidden';
    } else if (window.openModalCount > 0) {
      window.openModalCount--;
      window.modalZIndexBase = Math.max(1000, window.modalZIndexBase - 10);
      if (window.openModalCount === 0) {
        document.body.style.overflow = 'auto';
      }
    }

    return () => {
      if (show && window.openModalCount > 0) {
        window.openModalCount--;
        window.modalZIndexBase = Math.max(1000, window.modalZIndexBase - 10);
        if (window.openModalCount === 0) {
          document.body.style.overflow = 'auto';
        }
      }
    };
  }, [show]);

  // Move focus into the dialog on open.
  useEffect(() => {
    if (!show || !dialogRef.current) return;
    const first = dialogRef.current.querySelector(FOCUSABLE_SELECTOR);
    (first || dialogRef.current).focus();
  }, [show]);

  // Escape-to-close and keyboard focus trap.
  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  const sizeClass =
    size === 'sm' ? 'max-w-md' :
    size === 'lg' ? 'max-w-4xl' :
    size === 'xl' ? 'max-w-6xl' :
    size === '2xl' ? 'max-w-7xl' :
    size === 'full' ? 'max-w-full w-full h-full' :
    'max-w-2xl';

  if (!show) return null;

  const modalZIndex = window.modalZIndexBase || 1000;

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: modalZIndex }}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
          style={{ zIndex: 1 }}
        >
          <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
        </div>

        {/* Modal Dialog */}
        <div
          ref={dialogRef}
          tabIndex={-1}
          className={`inline-block align-bottom rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle w-full ${sizeClass} ${className} max-h-[90vh] overflow-y-auto border outline-none ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
          }`}
          style={{ zIndex: 10 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          {/* Header */}
          <div
            className={`px-6 py-4 border-b flex justify-between items-center sticky top-0 z-10 ${
              isDarkMode
                ? 'border-gray-700 bg-gray-800'
                : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'
            }`}
          >
            <h3 id="modal-headline" className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              {title}
            </h3>
            <div className="flex items-center gap-2">
              {headerActions}
              <button
                onClick={onClose}
                className={`transition-colors duration-150 focus:outline-none ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                }`}
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div
              className={`px-6 py-4 mt-2 border-t flex justify-end space-x-3 ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
